import { useEffect, useRef } from 'react';

/**
 * Модалка платного гейта. Открывается при клике на карточки prdBlockNumber 12/13
 * (Калькулятор / Договор) когда подписка `grace`/`expired`. В Phase 1
 * subscription state machine не реализована — модалка открывается всегда при
 * клике на эти блоки.
 *
 * UI per PRD §02 (`docs/prd/v4/02-onboarding.md` строки 90-104):
 * - Текст: «Для этой функции оплатите подписку» (фиксирован).
 * - 2 кнопки вертикально (P-019): «Оплатить» (yellow CTA) → 2.11.3,
 *   «Закрыть» (outline) → закрыть модалку.
 * - Закрытие: клик по overlay, Escape, кнопка «Закрыть».
 *
 * a11y per P-022: role="dialog", aria-modal, aria-labelledby связь с h2.
 *
 * Источник истины — прототип `screen-2-home.html` строки 475-490.
 */
export interface PaywallModalProps {
  onPay: () => void;
  onClose: () => void;
}

export function PaywallModal({ onPay, onClose }: PaywallModalProps) {
  const payButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    payButtonRef.current?.focus();
  }, []);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="paywall-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="paywall-modal-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="paywall-modal-title"
          className="font-bold text-ink leading-snug"
          style={{ fontSize: 22, margin: '0 0 24px' }}
        >
          Для этой функции оплатите подписку
        </h2>
        <button
          ref={payButtonRef}
          type="button"
          onClick={onPay}
          aria-label="Оплатить подписку"
          className={
            'w-full font-bold bg-cta text-ink border-b-2 border-cta-shadow ' +
            'min-h-tap-chip-lg rounded-2xl ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
            'focus-visible:outline-none active:opacity-90 transition-opacity'
          }
          style={{ marginBottom: 10, fontSize: 19 }}
        >
          Оплатить
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className={
            'w-full font-medium text-ink bg-transparent ' +
            'border-[1.5px] border-accent rounded-2xl ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
            'active:opacity-90 transition-opacity'
          }
          style={{ minHeight: 52, fontSize: 17 }}
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}
